import {
  Component,
  OnInit,
  inject,
  DestroyRef,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  ViewChild,
} from '@angular/core';

import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import {
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';

import { PaginatorControlComponent } from '@shared/components/paginator-control/paginator-control.component';
import { compressImageIfNeeded } from '@shared/utils/compress-image.util';

import { ButtonModule } from 'primeng/button';
import { PaginatorState } from 'primeng/paginator';
import { DrawerModule } from 'primeng/drawer';
import { SelectModule } from 'primeng/select';
import { DatePickerModule } from 'primeng/datepicker';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { SkeletonModule } from 'primeng/skeleton';
import { MenuModule } from 'primeng/menu';

import {
  ConfirmationService,
  MenuItem,
  MessageService,
} from 'primeng/api';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize, catchError, map, switchMap } from 'rxjs/operators';
import { forkJoin, of } from 'rxjs';

import { EmptyStateComponent } from '@shared/components/empty-state/empty-state.component';

import {
  EnumOcorrenciaCriticidade,
  EnumOcorrenciaEstado,
} from '@core/models/models';

import { EnumService } from '@shared/api/services/enum.service';

import {
  OcorrenciaService,
  RondaService,
  EfetivoService,
  PostoService,
  EscalaService,
  ClienteService,
  OcorrenciaAnexoService,
} from '@core/services/domain-services';

import {
  DetalheOcorrenciaComponent,
  OccurrenceData,
} from './detalhe-ocorrencia/detalhe-ocorrencia.component';
import { FecharOcorrenciaComponent } from './fechar-ocorrencia/fechar-ocorrencia.component';
import { AuthService } from '@auth/services/auth.service';

import {
  OcorrenciaEstatisticas,
  SideBarEstatisticas,
} from '@shared/api/services/estatisticas/estatisticas-info.service';

export interface OcorrenciaEstaModel {
  totalOcorrencias: number;
  abertas: number;
  fechadas: number;
  urgentes: number;
  altas: number;
  medias: number;
  incidentes: number;
  falhasEquipamento: number;
  intrusoes: number;
  emergencias: number;
  outros: number;
}

interface OcorrenciaItem {
  id: number;
  rondaId?: number;
  criticidade: string | number;
  tipoOcorrencia: string | number;
  descricaoTipoOutro?: string;
  dataOcorrencia: string;
  idPosto?: number;
  nomePosto?: string;
  posto?: any;
  nomeEfetivo?: string;
  efetivo?: any;
  registadoPor?: string;
  nomeRegistadoPor?: string;
  utilizador?: any;
  usuario?: any;
  criadoPor?: any;
  estado?: string;
  estadoEnum?: number;
}

interface SelectOption {
  label: string;
  value: string | number;
  postoId?: number;
}

@Component({
  selector: 'app-ocorrencias',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    DrawerModule,
    SelectModule,
    DatePickerModule,
    InputTextModule,
    TextareaModule,
    SkeletonModule,
    MenuModule,
    EmptyStateComponent,
    DetalheOcorrenciaComponent,
    FecharOcorrenciaComponent,
    PaginatorControlComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './ocorrencias.component.html',
  styleUrl: './ocorrencias.component.scss',
})
export class OcorrenciasComponent implements OnInit {
  @ViewChild('rowMenu')
  rowMenu!: any;

  private readonly fb = inject(FormBuilder);
  private readonly svc = inject(OcorrenciaService);
  readonly auth = inject(AuthService);
  private readonly anexoSvc = inject(OcorrenciaAnexoService);
  private readonly rondasvc = inject(RondaService);
  private readonly efetivosvc = inject(EfetivoService);
  private readonly escalasvc = inject(EscalaService);
  private readonly postosvc = inject(PostoService);
  private readonly clientesvc = inject(ClienteService);
  private readonly msg = inject(MessageService);
  private readonly confirm = inject(ConfirmationService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly enumService = inject(EnumService);
  private readonly route = inject(ActivatedRoute);
  private readonly ocorrenciaInfo = inject(OcorrenciaEstatisticas);
  private readonly sidebarEstatisticas = inject(SideBarEstatisticas);

  // QUERY PARAM
  private targetOccurrenceId: number | null = null;
  private openNewFromRoute = false;

  // DADOS DE ESTATÍSTICAS VINDOS DO BACKEND
  estatisticas: OcorrenciaEstaModel = {
    totalOcorrencias: 0,
    abertas: 0,
    fechadas: 0,
    urgentes: 0,
    altas: 0,
    medias: 0,
    incidentes: 0,
    falhasEquipamento: 0,
    intrusoes: 0,
    emergencias: 0,
    outros: 0,
  };

  ocorrencias: OcorrenciaItem[] = [];
  ocorrencia: OccurrenceData | null = null;

  // ESTADOS
  loading = false;
  showDialog = false;
  showDetalheOcorrencia = false;
  showFecharOcorrencia = false;
  ocorrenciaAEncerrar: OcorrenciaItem | null = null;
  ocorrenciaListaSelecionada: OcorrenciaItem | null = null;
  editing: any = null;
  detalheLoadingId: number | null = null;

  menuItems: MenuItem[] = [];

  fotoOcorrencia: File | null = null;
  fotoOcorrenciaPreview: string | null = null;

  // PAGINAÇÃO
  page = 1;
  pageSize = 10;
  totalRecords = 0;

  private currentQueryParams: Record<string, any> = {};

  // DROPDOWNS
  rondas: SelectOption[] = [];
  rondasFiltradas: SelectOption[] = [];
  tipos: SelectOption[] = [];
  postos: SelectOption[] = [];
  escalas: SelectOption[] = [];
  efetivos: SelectOption[] = [];
  clientes: SelectOption[] = [];

  // FILTROS
  activeCriticidadeFilter = 'TODAS';
  activeEstadoFilter: EnumOcorrenciaEstado | '' = '';

  private readonly criticidadeFiltroMap: Record<string, EnumOcorrenciaCriticidade> = {
    ALTA: EnumOcorrenciaCriticidade.ALTA,
    MEDIA: EnumOcorrenciaCriticidade.MEDIA,
    URGENTE: EnumOcorrenciaCriticidade.URGENTE,
  };

  private readonly estadoFiltroMap: Record<string, EnumOcorrenciaEstado> = {
    ABERTA: EnumOcorrenciaEstado.ABERTA,
    FECHADA: EnumOcorrenciaEstado.FECHADA,
    EM_PROGRESSO: EnumOcorrenciaEstado.EM_PROGRESSO,
  };

  // ÍCONES DOS TIPOS
  private readonly tipoIconMap: Record<string, string> = {
    INTRUSAO: 'ri-alarm-warning-line',
    AUSENCIA: 'ri-user-unfollow-line',
    AVARIA: 'ri-settings-4-line',
    RONDA_INCOMPLETA: 'ri-route-line',
    SUSPEITO: 'ri-spy-line',
    ACIDENTE: 'ri-alert-line',
    OUTRO: 'ri-chat-1-line',
  };

  // FORMULÁRIOS
  form!: FormGroup;
  filtrosForm!: FormGroup;
  filtrosPainelAberto = false;

  readonly criticidades = [
    { label: 'Média', value: EnumOcorrenciaCriticidade.MEDIA, icon: 'ri-information-line', css: 'crit-media' },
    { label: 'Alta', value: EnumOcorrenciaCriticidade.ALTA, icon: 'ri-alert-line', css: 'crit-alta' },
    { label: 'Urgente', value: EnumOcorrenciaCriticidade.URGENTE, icon: 'ri-alarm-warning-line', css: 'crit-urgente' },
  ];

  ngOnInit(): void {
    const targetId = Number(this.route.snapshot.queryParamMap.get('ocorrencia'));

    this.targetOccurrenceId = Number.isFinite(targetId) && targetId > 0 ? targetId : null;
    this.openNewFromRoute = this.route.snapshot.queryParamMap.get('novo') === 'true';

    this.initForm();
    if (this.openNewFromRoute) {
      this.openNewFromRoute = false;
      setTimeout(() => this.openNew(), 0);
    }
    this.loadOcorrencias();
    this.loadDropdowns();
    this.estatisticasOcorrencia();
  }

  // ESTATÍSTICAS DAS OCORRÊNCIAS VINDAS DO BACKEND
  private estatisticasOcorrencia(): void {
    this.ocorrenciaInfo
      .obterOcorrenEstatisticas()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (estatisticas: OcorrenciaEstaModel) => {
          this.estatisticas = { ...estatisticas };
          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error('Erro ao carregar estatísticas de ocorrências:', err);
        },
      });
  }

  // TRACK BY
  trackById(index: number, item: OcorrenciaItem): number {
    return item.id;
  }

  // FORMULÁRIOS
  initForm(): void {
    this.form = this.fb.group({
      idPosto: [null, Validators.required],
      idRonda: [null, Validators.required],
      criticidade: [null, Validators.required],
      tipoOcorrencia: [null, Validators.required],
      descricaoTipoOutro: [
        '',
        [
          Validators.required,
          Validators.minLength(10),
          Validators.maxLength(500),
        ],
      ],
      dataOcorrencia: [null, Validators.required],
    });

    this.filtrosForm = this.fb.group({
      IdRonda: [''],
      IdEscala: [''],
      IdPosto: [null],
      IdEfetivo: [null],
      IdCliente: [null],
      Criticidade: [''],
      TipoOcorrencia: [''],
      Data: [null],
    });
  }

  // DROPDOWNS
  loadDropdowns(): void {
    forkJoin({
      tipoOcorrencia: this.enumService.getTipoOcorrencia(),
      rondas: this.rondasvc.getAll({ pageSize: 100 }),
      postos: this.postosvc.getAll({ pageSize: 100 }),
      efetivos: this.efetivosvc.getAll({ pageSize: 100 }),
      escalas: this.escalasvc.getAll({ pageSize: 100 }),
      clientes: this.clientesvc.getAll({ pageSize: 100 }),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          if (response.tipoOcorrencia?.data) {
            this.tipos = Object.entries(response.tipoOcorrencia.data).map(
              ([key, label]) => ({
                value: key,
                label: label as string,
              }),
            );
          }

          if (response.rondas?.data) {
            this.rondas = response.rondas.data.map((item: any) => {
              const postoId = Number(
                item.idPosto ?? item.postoId ?? item.posto?.id,
              );

              return {
                label: `${item.nomePosto ?? '-'} - ${item.nomeEfetivo ?? '-'}`,
                value: item.id,
                postoId: Number.isFinite(postoId) ? postoId : undefined,
              };
            });
            this.rondasFiltradas = [...this.rondas];
          }

          if (response.postos?.data) {
            this.postos = response.postos.data.map((item: any) => ({
              label: item.nome,
              value: item.id,
            }));
          }

          if (response.efetivos?.data) {
            this.efetivos = response.efetivos.data.map((item: any) => ({
              label: item.nome,
              value: item.id,
            }));
          }

          if (response.escalas?.data) {
            this.escalas = response.escalas.data.map((item: any) => ({
              label: `${item.nomeTurno ?? '-'} - ${item.nomePosto ?? '-'}`,
              value: item.id,
            }));
          }

          if (response.clientes?.data) {
            this.clientes = response.clientes.data.map((item: any) => ({
              label: item.nome,
              value: item.id,
            }));
          }

          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error('Erro ao carregar dropdowns:', err);
          this.msg.add({
            severity: 'error',
            summary: 'Erro',
            detail: 'Não foi possível carregar os dados necessários',
            life: 5000,
          });
        },
      });
  }

  // LISTAGEM
  loadOcorrencias(queryParams: Record<string, any> = {}): void {
    this.loading = true;
    this.cdr.markForCheck();

    const incomingParams = { ...queryParams };

    const hasPaginationOnly = Object.keys(incomingParams).every(
      (key) =>
        key === 'pageNumber' || key === 'pageSize' || key === 'OrderBy',
    );

    if (Object.keys(incomingParams).length > 0 && !hasPaginationOnly) {
      const { pageNumber, pageSize, OrderBy, ...filters } = incomingParams;
      this.currentQueryParams = { ...filters };
    }

    const params: Record<string, any> = {
      ...this.currentQueryParams,
      pageNumber: queryParams['pageNumber'] ?? this.page,
      pageSize: queryParams['pageSize'] ?? this.pageSize,
      OrderBy: queryParams['OrderBy'] ?? 'id desc',
    };

    this.page = Number(params['pageNumber']) || 1;
    this.pageSize = Number(params['pageSize']) || this.pageSize;

    this.svc
      .getAll(params)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loading = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (res: any) => {
          const lista = (res?.data ?? []).map((item: any) =>
            this.normalizarOcorrencia(item),
          );

          this.ocorrencias = lista;
          this.preencherDadosDaOcorrencia(lista);

          this.totalRecords = this.obterTotalRecords(res);

          if (this.targetOccurrenceId) {
            const targetId = this.targetOccurrenceId;
            this.targetOccurrenceId = null;

            const target = this.ocorrencias.find((item: any) => item.id === targetId);

            this.openDetalhe(
              target ?? ({ id: targetId } as OcorrenciaItem),
            );
          }

          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error('Erro ao carregar ocorrências:', err);
          this.ocorrencias = [];
          this.totalRecords = 0;

          this.msg.add({
            severity: 'error',
            summary: 'Erro',
            detail: 'Não foi possível carregar as ocorrências',
            life: 5000,
          });

          this.cdr.markForCheck();
        },
      });
  }

  private normalizarOcorrencia(item: any): OcorrenciaItem {
    const posto = item?.posto;
    const efetivo = item?.efetivo;
    const utilizador = item?.utilizador ?? item?.usuario;
    const criadoPor = item?.criadoPor ?? item?.createdBy;
    const estadoEnum = this.mapearEstado(
      item?.estadoEnum ?? item?.estado ?? item?.estadoDescricao,
    );

    return {
      ...item,
      idPosto:
        item?.idPosto ?? item?.postoId ?? posto?.id ?? posto?.idPosto,
      nomePosto:
        item?.nomePosto || item?.postoNome || this.nomeDaEntidade(posto),
      nomeEfetivo:
        item?.nomeEfetivo || item?.efetivoNome || this.nomeDaEntidade(efetivo),
      registadoPor:
        this.nomeDaEntidade(item?.registadoPor) ??
        this.nomeDaEntidade(item?.nomeRegistadoPor) ??
        this.nomeDaEntidade(item?.registadoPorNome) ??
        this.nomeDaEntidade(item?.nomeUtilizador) ??
        this.nomeDaEntidade(utilizador) ??
        this.nomeDaEntidade(criadoPor) ??
        this.nomeDaEntidade(item?.utilizadorNome),
      estado:
        item?.estado || item?.estadoDescricao || item?.estadoEnum,
      estadoEnum,
    };
  }

  private mapearEstado(val: any): EnumOcorrenciaEstado | undefined {
    if (typeof val === 'number') {
      return this.normalizarParaEnum(val);
    }

    const texto = String(val ?? '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .toUpperCase()
      .replace(/\s+/g, '_');

    if (texto === 'ABERTA' || texto === 'ABERTO') {
      return EnumOcorrenciaEstado.ABERTA;
    }

    if (texto === 'EM_PROGRESSO') {
      return EnumOcorrenciaEstado.EM_PROGRESSO;
    }

    if (texto === 'FECHADA' || texto === 'FECHADO' || texto === 'RESOLVIDO') {
      return EnumOcorrenciaEstado.FECHADA;
    }

    return undefined;
  }

  private normalizarParaEnum(val: number): EnumOcorrenciaEstado | undefined {
    switch (val) {
      case EnumOcorrenciaEstado.ABERTA:
        return EnumOcorrenciaEstado.ABERTA;
      case EnumOcorrenciaEstado.FECHADA:
        return EnumOcorrenciaEstado.FECHADA;
      case EnumOcorrenciaEstado.EM_PROGRESSO:
        return EnumOcorrenciaEstado.EM_PROGRESSO;
      default:
        return undefined;
    }
  }

  private preencherDadosDaOcorrencia(lista: OcorrenciaItem[]): void {
    const linhasIncompletas = lista.filter(
      (item) => item.id && (!item.nomePosto || !item.nomeEfetivo),
    );

    if (!linhasIncompletas.length) {
      return;
    }

    forkJoin(
      linhasIncompletas.map((item) =>
        this.svc.getById(item.id).pipe(
          map((res: any) => ({
            ...item,
            ...this.normalizarOcorrencia(res?.data ?? res),
          })),
          catchError(() => of(item)),
        ),
      ),
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((linhasAtualizadas) => {
        const atualizadas = new Map(
          linhasAtualizadas.map((item) => [item.id, item]),
        );

        this.ocorrencias = this.ocorrencias.map(
          (item) => atualizadas.get(item.id) ?? item,
        );
        this.cdr.markForCheck();
      });
  }

  private nomeDaEntidade(value: any): string | undefined {
    if (typeof value === 'string' && value.trim()) {
      return value;
    }

    if (!value || typeof value !== 'object') {
      return undefined;
    }

    return (
      value.nome ??
      value.name ??
      value.nomeCompleto ??
      value.userName ??
      value.designacao
    );
  }

  getPostoNome(ocorrencia: OcorrenciaItem): string {
    return (
      ocorrencia.nomePosto ||
      this.nomeDaEntidade(ocorrencia.posto) ||
      this.postos.find((posto) => posto.value === ocorrencia.idPosto)?.label ||
      '-'
    );
  }

  getRegistadoPorNome(ocorrencia: OcorrenciaItem): string {
    return (
      ocorrencia.registadoPor ||
      ocorrencia.nomeRegistadoPor ||
      this.nomeDaEntidade(ocorrencia.utilizador) ||
      this.nomeDaEntidade(ocorrencia.usuario) ||
      this.nomeDaEntidade(ocorrencia.criadoPor) ||
      ocorrencia.nomeEfetivo ||
      this.nomeDaEntidade(ocorrencia.efetivo) ||
      '-'
    );
  }

  private obterTotalRecords(response: any): number {
    const total =
      response?.totalRecords ??
      response?.totalCount ??
      response?.total ??
      response?.count ??
      response?.meta?.total ??
      response?.pagination?.totalRecords ??
      response?.pagination?.total ??
      response?.data?.totalRecords ??
      response?.data?.totalCount;

    const numericTotal = Number(total);

    if (Number.isFinite(numericTotal) && numericTotal >= 0) {
      return numericTotal;
    }

    return this.ocorrencias.length;
  }

  onPageChange(event: PaginatorState): void {
    const newPage = (event.page ?? 0) + 1;
    const newPageSize = event.rows ?? this.pageSize;

    this.page = newPage;
    this.pageSize = newPageSize;
    this.loadOcorrencias({
      pageNumber: newPage,
      pageSize: newPageSize,
      OrderBy: 'id desc',
    });
  }

  get first(): number {
    return (this.page - 1) * this.pageSize;
  }

  private obterCriticidadeNumerica(value: any): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    if (typeof value === 'number') {
      return value;
    }

    const valor = String(value).toUpperCase();

    if (valor === 'ALTA') {
      return EnumOcorrenciaCriticidade.ALTA;
    }

    if (valor === 'MEDIA' || valor === 'MÉDIA') {
      return EnumOcorrenciaCriticidade.MEDIA;
    }

    if (valor === 'URGENTE') {
      return EnumOcorrenciaCriticidade.URGENTE;
    }

    const numero = Number(value);

    return Number.isFinite(numero) ? numero : null;
  }

  openNew(): void {
    this.editing = null;

    this.form.reset();
    this.rondasFiltradas = [];
    this.fotoOcorrencia = null;
    this.fotoOcorrenciaPreview = null;

    this.showDialog = true;
    this.cdr.markForCheck();
  }

  async onFotoOcorrenciaChange(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      this.msg.add({
        severity: 'warn',
        summary: 'Formato inválido',
        detail: 'Seleccione uma imagem.',
        life: 3000,
      });
      input.value = '';
      return;
    }
    const compressedFile = await compressImageIfNeeded(file);
    this.fotoOcorrencia = compressedFile;
    const reader = new FileReader();
    reader.onload = () => {
      this.fotoOcorrenciaPreview = String(reader.result);
      this.cdr.markForCheck();
    };
    reader.readAsDataURL(compressedFile);
  }

  openEdit(ocorrencia: OcorrenciaItem): void {
    this.editing = ocorrencia;

    const dataObj = ocorrencia.dataOcorrencia
      ? new Date(ocorrencia.dataOcorrencia)
      : null;
    const idPosto = Number(
      ocorrencia.idPosto ??
        (ocorrencia as any).postoId ??
        (ocorrencia as any).posto?.id,
    );
    const postoSelecionado = Number.isFinite(idPosto) ? idPosto : null;

    this.form.patchValue({
      idPosto: postoSelecionado,
      idRonda: ocorrencia.rondaId ?? null,
      criticidade: this.obterCriticidadeNumerica(ocorrencia.criticidade),
      tipoOcorrencia: ocorrencia.tipoOcorrencia,
      descricaoTipoOutro: ocorrencia.descricaoTipoOutro || '',
      dataOcorrencia: dataObj,
    });

    this.onPostoChange(postoSelecionado);
    this.form.patchValue({ idRonda: ocorrencia.rondaId ?? null });

    this.showDialog = true;
    this.cdr.markForCheck();
  }

  onPostoChange(idPosto: string | number | null): void {
    const postoId =
      idPosto === null || idPosto === undefined || idPosto === ''
        ? null
        : Number(idPosto);

    this.rondasFiltradas =
      postoId === null
        ? []
        : this.rondas.filter((ronda) => ronda.postoId === postoId);

    this.form.get('idRonda')?.reset();
    this.cdr.markForCheck();
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();

      this.msg.add({
        severity: 'warn',
        summary: 'Formulário inválido',
        detail: 'Preencha todos os campos obrigatórios corretamente',
        life: 3000,
      });

      return;
    }

    const formValue = this.form.value;

    const payload: any = {
      idRonda: formValue.idRonda ? Number(formValue.idRonda) : undefined,
      idPosto:
        formValue.idPosto !== null && formValue.idPosto !== undefined
          ? Number(formValue.idPosto)
          : undefined,
      criticidade:
        formValue.criticidade !== null && formValue.criticidade !== undefined
          ? Number(formValue.criticidade)
          : undefined,
      tipoOcorrencia:
        formValue.tipoOcorrencia !== null && formValue.tipoOcorrencia !== undefined
          ? Number(formValue.tipoOcorrencia)
          : undefined,
      descricaoTipoOutro: formValue.descricaoTipoOutro,
      dataOcorrencia:
        formValue.dataOcorrencia instanceof Date
          ? formValue.dataOcorrencia.toISOString()
          : formValue.dataOcorrencia,
    };

    const operation = this.editing
      ? this.svc.update(this.editing.id, payload)
      : this.svc.create(payload);

    operation
      .pipe(
        switchMap((res: any) => {
          const idOcorrencia = Number(
            res?.id ?? res?.data?.id ?? this.editing?.id,
          );
          if (this.editing || !this.fotoOcorrenciaPreview || !idOcorrencia)
            return of(res);
          return this.anexoSvc
            .create({
              idOcorrencia,
              idAnexo: 0,
              url: this.fotoOcorrenciaPreview,
            })
            .pipe(map(() => res));
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          this.msg.add({
            severity: 'success',
            summary: 'Sucesso',
            detail: this.editing
              ? 'Ocorrência atualizada com sucesso'
              : 'Ocorrência registada com sucesso',
            life: 3000,
          });

          this.showDialog = false;
          this.loadOcorrencias();
          this.estatisticasOcorrencia();
          this.sidebarEstatisticas.requestRefresh();
        },
        error: (err) => {
          console.error('Erro ao salvar:', err);

          this.msg.add({
            severity: 'error',
            summary: 'Erro',
            detail: 'Não foi possível salvar a ocorrência',
            life: 5000,
          });
        },
      });
  }

  openRowMenu(event: Event, ocorrencia: OcorrenciaItem): void {
    this.ocorrenciaListaSelecionada = ocorrencia;
    const estadoEnum = ocorrencia.estadoEnum;
    const isFechada = estadoEnum === EnumOcorrenciaEstado.FECHADA;

    this.menuItems = [];

    this.menuItems.push({
      label: 'Ver detalhes',
      icon: 'pi pi-eye',
      command: () => this.openDetalhe(ocorrencia),
    });

    if (!isFechada) {
      this.menuItems.push({
        label: 'Editar',
        icon: 'pi pi-pencil',
        command: () => this.openEdit(ocorrencia),
      });
      if (this.auth.hasRole('FECHAR_OCORRENCIA')) {
        this.menuItems.push({
          label: 'Fechar ocorrência',
          icon: 'pi pi-check-circle',
          command: () => this.openFecharOcorrencia(ocorrencia),
        });
      }
    } else {
      if (this.auth.hasRole('REABRIR_OCORRENCIA')) {
        this.menuItems.push({
          label: 'Reabrir ocorrência',
          icon: 'pi pi-refresh',
          command: () => this.confirmReabrir(ocorrencia),
        });
      }
    }

    this.menuItems.push({
      separator: true,
    });

    this.menuItems.push({
      label: 'Eliminar',
      icon: 'pi pi-trash',
      styleClass: 'menu-danger',
      command: () => this.confirmDelete(ocorrencia),
    });

    this.rowMenu.toggle(event);
  }

  confirmReabrir(ocorrencia: any): void {
    const alvo = this.ocorrenciaListaSelecionada || ocorrencia;
    if (!alvo?.id) return;
    this.confirm.confirm({
      header: 'Reabrir Ocorrência',
      message: 'Tem a certeza que deseja reabrir esta ocorrência?',
      acceptLabel: 'Sim, reabrir',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: 'p-button-warning',
      accept: () => {
        this.svc
          .reabrir(alvo.id!)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => {
              this.msg.add({
                severity: 'success',
                summary: 'Ocorrência reaberta',
                detail: 'A ocorrência foi reaberta com sucesso.',
              });
              this.loadOcorrencias();
              this.estatisticasOcorrencia();
              if (this.ocorrencia?.id === alvo.id) {
                this.openDetalhe({ id: alvo.id } as OcorrenciaItem);
              }
              this.cdr.markForCheck();
            },
            error: (err) => {
              const mensagem =
                err?.error?.retorno?.mensagem ||
                err?.error?.message ||
                err?.message ||
                'Não foi possível reabrir a ocorrência.';
              this.msg.add({
                severity: 'error',
                summary: 'Erro ao reabrir ocorrência',
                detail: mensagem,
              });
            },
          });
      },
    });
  }

  openFecharOcorrencia(ocorrencia: any): void {
    const alvo = this.ocorrenciaListaSelecionada || ocorrencia || this.ocorrenciaAEncerrar;
    if (!alvo?.id) return;
    this.ocorrenciaAEncerrar = alvo as OcorrenciaItem;
    this.showDetalheOcorrencia = false;
    this.cdr.markForCheck();
    setTimeout(() => {
      this.showFecharOcorrencia = true;
      this.cdr.markForCheck();
    });
  }

  onOcorrenciaFechada(id: number): void {
    this.loadOcorrencias();
    this.estatisticasOcorrencia();
    if (this.ocorrencia?.id === id) {
      this.openDetalhe({ id, estadoEnum: 2 } as OcorrenciaItem);
    }
    this.cdr.markForCheck();
  }

  confirmDelete(ocorrencia: OcorrenciaItem): void {
    this.confirm.confirm({
      header: 'Eliminar Ocorrência',
      message: `Tem certeza que deseja eliminar a ocorrência do tipo ${this.getTipoLabel(
        ocorrencia.tipoOcorrencia,
      )}?`,
      acceptLabel: 'Sim, eliminar',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        if (!ocorrencia.id) {
          return;
        }

        this.svc
          .delete(ocorrencia.id)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => {
              this.msg.add({
                severity: 'success',
                summary: 'Eliminado',
                detail: 'Ocorrência eliminada com sucesso',
                life: 3000,
              });

              this.loadOcorrencias();
              this.estatisticasOcorrencia();
            },
            error: (err) => {
              console.error('Erro ao eliminar:', err);

              this.msg.add({
                severity: 'error',
                summary: 'Erro',
                detail: 'Não foi possível eliminar a ocorrência',
                life: 5000,
              });
            },
          });
      },
    });
  }

  limparFiltros(): void {
    this.filtrosForm.reset({
      IdRonda: '',
      IdEscala: '',
      IdPosto: null,
      IdEfetivo: null,
      IdCliente: null,
      Criticidade: '',
      TipoOcorrencia: '',
      Data: null,
    });

    this.activeCriticidadeFilter = 'TODAS';
    this.activeEstadoFilter = '';
    this.currentQueryParams = {};
    this.page = 1;
    this.filtrosPainelAberto = true;
  }

  aplicarFiltrosDrawer(): void {
    const params = this.buildOccurrenceFilterParams(this.filtrosForm.value);

    this.filtrosPainelAberto = false;
    this.aplicarFiltros(params);
  }

  private buildOccurrenceFilterParams(value: Record<string, any>): Record<string, any> {
    const params: Record<string, any> = {};
    Object.entries(value).forEach(([key, val]) => {
      if (key !== 'Data' && val !== null && val !== undefined && val !== '') {
        params[key] = val;
      }
    });

    if (value['Data'] !== null && value['Data'] !== undefined && value['Data'] !== '') {
      params['DataDe'] = value['Data'];
      params['DataAte'] = value['Data'];
    }
    return params;
  }

  aplicarFiltros(filtros: Record<string, any>): void {
    this.page = 1;
    this.currentQueryParams = { ...filtros };

    this.loadOcorrencias({
      pageNumber: 1,
      pageSize: this.pageSize,
      ...filtros,
    });
  }

  setActiveCriticidade(crit: string): void {
    this.activeCriticidadeFilter = crit;
    this.aplicarFiltrosChips();
  }

  onEstadoFilterChange(event: Event): void {
    const value = (event.target as HTMLSelectElement).value;

    if (value === 'Todos') {
      this.activeEstadoFilter = '';
    } else {
      this.activeEstadoFilter = this.estadoFiltroMap[value] ?? '';
    }

    this.aplicarFiltrosChips();
  }

  aplicarFiltrosChips(): void {
    const formVal = this.filtrosForm.value;
    const queryParams = this.buildOccurrenceFilterParams(formVal);

    if (this.activeCriticidadeFilter !== 'TODAS') {
      const criticidade = this.criticidadeFiltroMap[
        this.activeCriticidadeFilter
      ];

      if (criticidade !== undefined) {
        queryParams['Criticidade'] = criticidade;
      }
    } else {
      delete queryParams['Criticidade'];
    }

    if (this.activeEstadoFilter) {
      queryParams['EstadoEnum'] = this.activeEstadoFilter;
    } else {
      delete queryParams['EstadoEnum'];
    }

    this.page = 1;
    this.currentQueryParams = { ...queryParams };

    if (!this.activeEstadoFilter) {
      delete this.currentQueryParams['EstadoEnum'];
    }

    this.loadOcorrencias({
      pageNumber: 1,
      pageSize: this.pageSize,
      ...queryParams,
    });
  }

  setCriticidade(val: any): void {
    this.form.get('criticidade')?.setValue(val);
  }

  getCriticidadeLabel(v: any): string {
    const valor = this.obterCriticidadeNumerica(v);

    if (valor === EnumOcorrenciaCriticidade.MEDIA) {
      return 'Média';
    }

    if (valor === EnumOcorrenciaCriticidade.ALTA) {
      return 'Alta';
    }

    if (valor === EnumOcorrenciaCriticidade.URGENTE) {
      return 'Urgente';
    }

    return String(v ?? '');
  }

  getCriticidadeClass(v: any): string {
    const valor = this.obterCriticidadeNumerica(v);

    if (valor === EnumOcorrenciaCriticidade.MEDIA) {
      return 'crit-badge-media';
    }

    if (valor === EnumOcorrenciaCriticidade.ALTA) {
      return 'crit-badge-alta';
    }

    if (valor === EnumOcorrenciaCriticidade.URGENTE) {
      return 'crit-badge-urgente';
    }

    return '';
  }

  getTipoLabel(v: any): string {
    const tipo = this.tipos.find((t) => String(t.value) === String(v));

    return tipo?.label ?? String(v ?? '');
  }

  getTipoIcon(v: any): string {
    const valor = String(v ?? '').toUpperCase();

    return this.tipoIconMap[valor] ?? 'ri-question-line';
  }

  isFieldInvalid(name: string): boolean {
    const control = this.form.get(name);

    return !!(control && control.invalid && (control.touched || control.dirty));
  }

  getFieldError(name: string): string {
    const control = this.form.get(name);

    if (!control || !control.errors) {
      return '';
    }

    if (control.errors['required']) {
      return 'Este campo de preenchimento obrigatório.';
    }

    if (control.errors['minlength']) {
      return `O texto deve conter pelo menos ${control.errors['minlength'].requiredLength} caracteres.`;
    }

    if (control.errors['maxlength']) {
      return `O texto não pode exceder ${control.errors['maxlength'].requiredLength} caracteres.`;
    }

    return 'Campo inválido.';
  }

  getFormErrors(): string[] {
    const errors: string[] = [];

    Object.keys(this.form.controls).forEach((key) => {
      const control = this.form.get(key);

      if (control?.invalid && control?.touched) {
        errors.push(`${key}: ${JSON.stringify(control.errors)}`);
      }
    });

    return errors;
  }

  openDetalhe(ocorrencia: OcorrenciaItem): void {
    this.ocorrenciaListaSelecionada = ocorrencia;
    if (!ocorrencia?.id) {
      return;
    }

    this.detalheLoadingId = ocorrencia.id;
    this.cdr.markForCheck();

    this.svc
      .getById(ocorrencia.id)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.detalheLoadingId = null;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (res: any) => {
          const detalhe = res?.data ?? res;

          this.ocorrencia = {
            ...detalhe,
            estado: detalhe.estado ?? ocorrencia.estado ?? null,
            criticidade: this.getCriticidadeLabel(detalhe.criticidade),
            anexos: detalhe.anexos ?? [],
          };

          this.showDetalheOcorrencia = true;
          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error('Erro ao carregar detalhe da ocorrência:', err);

          this.msg.add({
            severity: 'error',
            summary: 'Erro',
            detail: 'Não foi possível carregar o detalhe da ocorrência',
            life: 5000,
          });
        },
      });
  }
}
